import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { updateStoreStock, softDeleteStoreStock, getCardgames, getProductTypes, createCustomProduct } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { AddCatalogProductModal } from './AddCatalogProductModal';

interface StockItem {
  id: number;
  product_id: number;
  quantity: number;
  store_price: number | null;
  pre_sale: boolean;
  products: {
    id: number;
    name: string;
    slug: string;
    image_url: string | null;
    game_id?: number | null;
    product_type_id?: number | null;
    games: { id: number; name: string } | null;
  };
}

export const getTicketInfo = (item: StockItem) => {
  const p = item.products;
  if (!p) return null;
  const isTicketType = p.product_type_id === 52 || p.product_type_id === 3;
  const name = (p.name || '').toLowerCase();
  const isTicketName = name.includes('ticket') || name.includes('ingresso') || name.includes('inscrição') || name.includes('inscricao');

  if (isTicketType || isTicketName) {
    const rawName = p.name || 'Ingresso';
    const tourneyName = rawName.replace(/^(Inscrição:\s*|Ingresso:\s*)/i, '').trim();
    return {
      isTicket: true,
      maxPlayers: Number(item.quantity) || 8,
      tournamentName: tourneyName || null
    };
  }
  return null;
};

export const ManageStock: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const location = useLocation();

  // Core Data
  const [stock, setStock] = useState<StockItem[]>([]);
  const [games, setGames] = useState<any[]>([]);
  const [productTypes, setProductTypes] = useState<any[]>([]);
  const [store, setStore] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit Buffer: { [productId]: { quantity, price, preSale } }
  const [editingStock, setEditingStock] = useState<Record<number, { quantity: number; price: number; preSale: boolean }>>({});
  const [savingMap, setSavingMap] = useState<Record<number, boolean>>({});
  const [savingAll, setSavingAll] = useState(false);

  // Filters & View State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGameId, setSelectedGameId] = useState<number | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'in_stock' | 'out_of_stock' | 'pre_sale' | 'tickets'>('all');
  const [sortBy, setSortBy] = useState<'name_asc' | 'name_desc' | 'qty_desc' | 'qty_asc' | 'price_desc' | 'price_asc'>('name_asc');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() => {
    return (localStorage.getItem('cardumy_stock_view') as 'table' | 'cards') || 'table';
  });
  const [pageSize, setPageSize] = useState<number | 'all'>(50);
  const [currentPage, setCurrentPage] = useState(1);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [customProductData, setCustomProductData] = useState({
    name: '',
    description: '',
    image_url: '',
    game_id: '',
    product_type_id: '',
    mspr: '',
    quantity: '1'
  });
  const [submitting, setSubmitting] = useState(false);

  // Sync viewMode preference
  const handleToggleViewMode = (mode: 'table' | 'cards') => {
    setViewMode(mode);
    localStorage.setItem('cardumy_stock_view', mode);
  };

  // URL query triggers
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('adicionar') === 'catalogo' || params.get('action') === 'add-catalog') {
      setIsCatalogModalOpen(true);
    }
  }, [location.search]);

  // Fetch games & product types
  useEffect(() => {
    const fetchGamesAndTypes = async () => {
      try {
        const [gameList, typesList] = await Promise.all([
          getCardgames(),
          getProductTypes()
        ]);
        setGames(gameList || []);
        setProductTypes(typesList || []);
      } catch (e) {
        console.error("Error fetching games or types:", e);
      }
    };
    fetchGamesAndTypes();
  }, []);

  // Fetch store and store stock
  useEffect(() => {
    const fetchStoreAndStock = async () => {
      if (!user?.username) return;
      try {
        setLoading(true);
        // 1. Get Store Info via store_members
        const { data: storeData, error: storeError } = await supabase
          .from('stores')
          .select('*, store_members!inner(user_id)')
          .eq('store_members.user_id', user.id)
          .maybeSingle();

        if (storeError) throw storeError;
        if (!storeData) throw new Error("Loja não encontrada para este usuário.");
        setStore(storeData);

        // 2. Get Stock
        const { data: stockData, error: stockError } = await supabase
          .from('store_products')
          .select(`
            *,
            products:products(
              id,
              name,
              slug,
              image_url,
              game_id,
              product_type_id,
              games:games(id, name)
            )
          `)
          .eq('store_id', storeData.id)
          .is('deleted_at', null);

        if (stockError) throw stockError;

        if (Array.isArray(stockData)) {
          setStock(stockData as any);
          const initialEditingState: Record<number, { quantity: number; price: number; preSale: boolean }> = {};
          stockData.forEach(item => {
            initialEditingState[item.product_id] = {
              quantity: item.quantity,
              price: item.store_price ? Math.round(Number(item.store_price) * 100) / 100 : 0,
              preSale: !!item.pre_sale
            };
          });
          setEditingStock(initialEditingState);
        } else {
          setStock([]);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStoreAndStock();
  }, [user]);

  // Handle addition from catalog modal
  const handleStockFromCatalog = (updatedItem: any) => {
    setStock(prev => {
      const exists = prev.find(i => i.product_id === updatedItem.product_id);
      if (exists) {
        return prev.map(i => i.product_id === updatedItem.product_id ? {
          ...i,
          quantity: updatedItem.quantity,
          store_price: updatedItem.store_price,
          pre_sale: updatedItem.pre_sale
        } : i);
      } else {
        const newItem: StockItem = {
          id: updatedItem.product_id,
          product_id: updatedItem.product_id,
          quantity: updatedItem.quantity,
          store_price: updatedItem.store_price,
          pre_sale: updatedItem.pre_sale,
          products: updatedItem.product
        };
        return [newItem, ...prev];
      }
    });

    setEditingStock(prev => ({
      ...prev,
      [updatedItem.product_id]: {
        quantity: updatedItem.quantity,
        price: updatedItem.store_price,
        preSale: updatedItem.pre_sale
      }
    }));
  };

  // Local editing change
  const handleLocalChange = (productId: number, field: string, value: any) => {
    if (field === 'quantity') {
      const currentItem = stock.find(s => s.product_id === productId);
      const ticketInfo = currentItem ? getTicketInfo(currentItem) : null;
      if (currentItem && ticketInfo) {
        if (Number(value) > currentItem.quantity) {
          showNotification(`A quantia de ingressos é definida na criação do torneio (${ticketInfo.maxPlayers} vagas máx) e não pode ser aumentada no estoque.`, "info");
          return;
        }
      }
    }

    setEditingStock(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
  };

  // Check if an item has unsaved changes
  const checkIsDirty = (item: StockItem) => {
    const edit = editingStock[item.product_id];
    if (!edit) return false;
    const originalPrice = item.store_price ? Math.round(Number(item.store_price) * 100) / 100 : 0;
    return edit.quantity !== item.quantity || edit.price !== originalPrice || edit.preSale !== !!item.pre_sale;
  };

  // All dirty products
  const dirtyItems = useMemo(() => {
    return stock.filter(item => checkIsDirty(item));
  }, [stock, editingStock]);

  // Save single item
  const handleSave = async (productId: number) => {
    if (!store?.id) return;
    const editData = editingStock[productId];
    if (!editData) return;

    const currentItem = stock.find(s => s.product_id === productId);
    const ticketInfo = currentItem ? getTicketInfo(currentItem) : null;
    if (currentItem && ticketInfo && Number(editData.quantity) > currentItem.quantity) {
      showNotification(`O limite de ingressos é de ${ticketInfo.maxPlayers} vagas definido na criação do torneio.`, "info");
      return;
    }

    if (editData.price === undefined || editData.price === null || Number(editData.price) < 0.05) {
      showNotification("O valor mínimo de um produto no estoque é de R$ 0,05 (5 centavos).", "error");
      return;
    }

    setSavingMap(prev => ({ ...prev, [productId]: true }));
    try {
      const validPrice = Math.max(0.05, Math.round(Number(editData.price) * 100) / 100);
      const targetQty = (currentItem && ticketInfo) ? Math.min(currentItem.quantity, editData.quantity) : editData.quantity;
      const result = await updateStoreStock(store.id, productId, targetQty, validPrice, editData.preSale);

      if (!result || (result as any).error) throw new Error((result as any)?.error || "Falha ao atualizar estoque");

      showNotification("Item atualizado com sucesso!", "success");

      setStock(prev => prev.map(item =>
        item.product_id === productId
          ? { ...item, quantity: targetQty, store_price: validPrice, pre_sale: editData.preSale }
          : item
      ));
    } catch (err: any) {
      showNotification(err.message, "error");
    } finally {
      setSavingMap(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Bulk save all modified items
  const handleSaveAll = async () => {
    if (!store?.id || dirtyItems.length === 0) return;
    setSavingAll(true);
    let successCount = 0;
    let errorCount = 0;

    for (const item of dirtyItems) {
      const editData = editingStock[item.product_id];
      if (!editData) continue;
      const ticketInfo = getTicketInfo(item);
      const validPrice = Math.max(0.05, Math.round(Number(editData.price || 0.05) * 100) / 100);
      const targetQty = ticketInfo ? Math.min(item.quantity, editData.quantity) : editData.quantity;

      try {
        const res = await updateStoreStock(store.id, item.product_id, targetQty, validPrice, editData.preSale);
        if (res && !(res as any).error) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (e) {
        errorCount++;
      }
    }

    // Refresh local stock with updated values
    setStock(prev => prev.map(item => {
      const edit = editingStock[item.product_id];
      if (!edit) return item;
      return {
        ...item,
        quantity: edit.quantity,
        store_price: Math.max(0.05, Math.round(Number(edit.price) * 100) / 100),
        pre_sale: edit.preSale
      };
    }));

    setSavingAll(false);
    if (errorCount === 0) {
      showNotification(`${successCount} produto(s) atualizado(s) com sucesso!`, "success");
    } else {
      showNotification(`${successCount} produtos salvos, ${errorCount} com erro.`, "info");
    }
  };

  // Discard all changes
  const handleDiscardAll = () => {
    const resetState: Record<number, { quantity: number; price: number; preSale: boolean }> = {};
    stock.forEach(item => {
      resetState[item.product_id] = {
        quantity: item.quantity,
        price: item.store_price ? Math.round(Number(item.store_price) * 100) / 100 : 0,
        preSale: !!item.pre_sale
      };
    });
    setEditingStock(resetState);
    showNotification("Alterações descartadas.", "info");
  };

  // Remove item
  const handleRemove = async (productId: number) => {
    if (!store?.id) return;

    const confirmResult = await Swal.fire({
      title: 'Remover produto?',
      text: 'Deseja realmente remover este produto do seu estoque?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#475569',
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
      background: '#0f172a',
      color: '#f8fafc',
      customClass: {
        popup: 'rounded-2xl border border-slate-800'
      }
    });

    if (!confirmResult.isConfirmed) return;

    try {
      const result = await softDeleteStoreStock(store.id, productId);
      if (!result || (result as any).error) throw new Error((result as any).error || "Falha ao remover item");

      showNotification("Produto removido do estoque!", "success");

      setEditingStock(prev => {
        const copy = { ...prev };
        delete copy[productId];
        return copy;
      });

      setStock(prev => prev.filter(item => item.product_id !== productId));
    } catch (err: any) {
      showNotification(err.message, "error");
    }
  };

  // Create custom product
  const handleCreateCustomProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!store?.id) return;
    if (!customProductData.name || !customProductData.product_type_id || !customProductData.mspr || !customProductData.quantity) {
      showNotification("Por favor, preencha todos os campos obrigatórios.", "error");
      return;
    }

    const parsedPrice = Number(customProductData.mspr);
    if (isNaN(parsedPrice) || parsedPrice < 0.05) {
      showNotification("O valor mínimo de um produto no estoque é de R$ 0,05 (5 centavos).", "error");
      return;
    }

    setSubmitting(true);
    try {
      const finalPrice = Math.max(0.05, parsedPrice);
      const pData = {
        name: customProductData.name,
        description: customProductData.description,
        image_url: customProductData.image_url || 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=400',
        game_id: customProductData.game_id ? Number(customProductData.game_id) : null,
        product_type_id: Number(customProductData.product_type_id),
        mspr: finalPrice,
        quantity: Number(customProductData.quantity)
      };

      const result = await createCustomProduct(store.id, pData);
      if (result && result.success && result.product) {
        showNotification("Produto personalizado adicionado ao estoque!", "success");
        setIsModalOpen(false);
        setCustomProductData({
          name: '',
          description: '',
          image_url: '',
          game_id: '',
          product_type_id: '',
          mspr: '',
          quantity: '1'
        });

        const newStockItem: StockItem = {
          id: Date.now(),
          product_id: result.product.id,
          quantity: pData.quantity,
          store_price: pData.mspr,
          pre_sale: false,
          products: {
            id: result.product.id,
            name: result.product.name,
            slug: result.product.slug,
            image_url: result.product.image_url,
            game_id: result.product.game_id,
            games: games.find(g => g.id === result.product.game_id) || null
          }
        };

        setStock(prev => [newStockItem, ...prev]);
        setEditingStock(prev => ({
          ...prev,
          [result.product.id]: {
            quantity: pData.quantity,
            price: pData.mspr,
            preSale: false
          }
        }));
      } else {
        throw new Error(result?.error || "Erro ao criar produto customizado.");
      }
    } catch (err: any) {
      showNotification(err.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Summary Metrics
  const metrics = useMemo(() => {
    let totalUnits = 0;
    let totalValue = 0;
    let outOfStock = 0;
    let preSaleCount = 0;
    let ticketCount = 0;

    stock.forEach(item => {
      const qty = item.quantity || 0;
      const price = item.store_price || 0;
      totalUnits += qty;
      totalValue += qty * price;
      if (qty === 0) outOfStock++;
      if (item.pre_sale) preSaleCount++;
      if (getTicketInfo(item)) ticketCount++;
    });

    return {
      totalItems: stock.length,
      totalUnits,
      totalValue,
      outOfStock,
      preSaleCount,
      ticketCount
    };
  }, [stock]);

  // Filtered & Sorted Stock
  const processedStock = useMemo(() => {
    let result = [...stock];

    // Search filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      result = result.filter(item => {
        const name = (item.products?.name || '').toLowerCase();
        const idMatch = String(item.product_id) === q;
        const gameMatch = (item.products?.games?.name || '').toLowerCase().includes(q);
        return name.includes(q) || idMatch || gameMatch;
      });
    }

    // Game filter
    if (selectedGameId !== 'all') {
      result = result.filter(item => {
        const itemGameId = item.products?.game_id || item.products?.games?.id;
        return Number(itemGameId) === Number(selectedGameId);
      });
    }

    // Status filter
    if (statusFilter === 'in_stock') {
      result = result.filter(item => (editingStock[item.product_id]?.quantity ?? item.quantity) > 0);
    } else if (statusFilter === 'out_of_stock') {
      result = result.filter(item => (editingStock[item.product_id]?.quantity ?? item.quantity) === 0);
    } else if (statusFilter === 'pre_sale') {
      result = result.filter(item => editingStock[item.product_id]?.preSale ?? item.pre_sale);
    } else if (statusFilter === 'tickets') {
      result = result.filter(item => !!getTicketInfo(item));
    }

    // Sorting
    result.sort((a, b) => {
      const nameA = a.products?.name || '';
      const nameB = b.products?.name || '';
      const qtyA = editingStock[a.product_id]?.quantity ?? a.quantity;
      const qtyB = editingStock[b.product_id]?.quantity ?? b.quantity;
      const priceA = editingStock[a.product_id]?.price ?? a.store_price ?? 0;
      const priceB = editingStock[b.product_id]?.price ?? b.store_price ?? 0;

      switch (sortBy) {
        case 'name_asc': return nameA.localeCompare(nameB, 'pt-BR');
        case 'name_desc': return nameB.localeCompare(nameA, 'pt-BR');
        case 'qty_desc': return qtyB - qtyA;
        case 'qty_asc': return qtyA - qtyB;
        case 'price_desc': return priceB - priceA;
        case 'price_asc': return priceA - priceB;
        default: return 0;
      }
    });

    return result;
  }, [stock, searchTerm, selectedGameId, statusFilter, sortBy, editingStock]);

  // Paginated Stock
  const totalPages = pageSize === 'all' ? 1 : Math.ceil(processedStock.length / pageSize) || 1;
  const paginatedStock = useMemo(() => {
    if (pageSize === 'all') return processedStock;
    const start = (currentPage - 1) * pageSize;
    return processedStock.slice(start, start + pageSize);
  }, [processedStock, pageSize, currentPage]);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGameId, statusFilter, pageSize]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[460px] space-y-3">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
        <p className="text-slate-400 text-xs font-medium">Carregando estoque da loja...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto my-12 bg-rose-500/10 border border-rose-500/30 p-6 rounded-2xl text-center space-y-3">
        <i className="fas fa-triangle-exclamation text-rose-400 text-2xl"></i>
        <h2 className="text-white font-bold text-sm">Não foi possível carregar o estoque</h2>
        <p className="text-rose-300/80 text-xs">{error}</p>
        <Link to="/inicio" className="inline-block mt-2 text-xs font-bold text-purple-400 hover:underline">
          Voltar para o Painel
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 space-y-4 py-2">
      {/* Top Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-800 pb-4">
        <div>
          <div className="flex items-center space-x-2 text-[11px] font-bold text-purple-400 uppercase tracking-widest mb-1">
            <Link to="/minha-loja" className="hover:underline">Minha Loja</Link>
            <span className="text-slate-600">/</span>
            <span className="text-slate-400">Estoque de Produtos</span>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-black text-white italic tracking-tight uppercase">
              Gerenciar Estoque
            </h1>
            <span className="text-xs font-mono font-bold text-slate-400 bg-slate-900 border border-slate-800 px-2.5 py-0.5 rounded-lg">
              {metrics.totalItems} produtos
            </span>
          </div>
          <p className="text-slate-400 text-xs mt-0.5">
            Ajuste preços, controle quantidades e adicione novos itens para venda
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={() => setIsCatalogModalOpen(true)}
            className="bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs px-4 py-2 rounded-xl transition-colors flex items-center space-x-2 cursor-pointer"
          >
            <i className="fas fa-bag-shopping text-xs"></i>
            <span>Adicionar do Catálogo</span>
          </button>

          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="bg-slate-900 hover:bg-slate-850 text-slate-200 hover:text-white border border-slate-700 font-bold text-xs px-3.5 py-2 rounded-xl transition-colors flex items-center space-x-2 cursor-pointer"
          >
            <i className="fas fa-plus text-xs text-purple-400"></i>
            <span>Produto Avulso</span>
          </button>
        </div>
      </div>

      {/* Sales Disabled Alert */}
      {store && store.sales_enabled === false && (
        <div className="bg-amber-500/10 border border-amber-500/30 px-4 py-3 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-amber-200">
          <div className="flex items-center gap-3">
            <i className="fas fa-store-slash text-amber-400 text-base shrink-0"></i>
            <div>
              <p className="text-xs font-bold text-white">Vendas na loja pausadas no momento</p>
              <p className="text-[11px] text-amber-200/80">
                Seus produtos permanecem visíveis no catálogo, mas novos pedidos estão bloqueados até reativar as vendas.
              </p>
            </div>
          </div>
          <Link
            to="/minha-loja"
            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black rounded-xl text-[11px] uppercase tracking-wider whitespace-nowrap transition-colors"
          >
            Ativar Vendas
          </Link>
        </div>
      )}

      {/* Metric Cards Strip (Lightweight, No shadows, No GPU blurs) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-3.5 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Total de Produtos</span>
            <span className="text-xl font-black text-white font-mono">{metrics.totalItems}</span>
          </div>
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center text-xs">
            <i className="fas fa-boxes-stacked"></i>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-3.5 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Unidades em Estoque</span>
            <span className="text-xl font-black text-white font-mono">{metrics.totalUnits}</span>
          </div>
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs">
            <i className="fas fa-cubes"></i>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-3.5 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Valor em Estoque</span>
            <span className="text-xl font-black text-white font-mono">
              R$ {metrics.totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs">
            <i className="fas fa-receipt"></i>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-3.5 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Esgotados</span>
            <span className={`text-xl font-black font-mono ${metrics.outOfStock > 0 ? 'text-amber-400' : 'text-slate-400'}`}>
              {metrics.outOfStock}
            </span>
          </div>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs border ${
            metrics.outOfStock > 0 
              ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' 
              : 'bg-slate-800 border-slate-700 text-slate-500'
          }`}>
            <i className="fas fa-triangle-exclamation"></i>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Banner (Clean, Zero pulse animation) */}
      {dirtyItems.length > 0 && (
        <div className="bg-purple-950/40 border border-purple-500/40 px-4 py-2.5 rounded-xl flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-2 text-xs text-purple-200">
            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
            <span>
              Você possui <strong>{dirtyItems.length} produto(s)</strong> com alterações não salvas.
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleDiscardAll}
              disabled={savingAll}
              className="px-3 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs font-bold transition-colors border border-slate-700 cursor-pointer disabled:opacity-50"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={savingAll}
              className="px-3.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-wider transition-colors flex items-center space-x-1.5 cursor-pointer disabled:opacity-50"
            >
              {savingAll ? (
                <>
                  <i className="fas fa-spinner fa-spin text-xs"></i>
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-check text-xs"></i>
                  <span>Salvar Todas ({dirtyItems.length})</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Control Bar: Search, Game Filter, Status Filter & View Toggle */}
      <div className="bg-slate-900 border border-slate-800 p-3 rounded-xl space-y-2.5">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2.5">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[240px]">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome do produto, ID ou modalidade..."
              className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500/70 text-slate-200 placeholder:text-slate-500 pl-9 pr-8 py-2 rounded-lg text-xs font-medium outline-none transition-colors"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs cursor-pointer"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Game Selector */}
            {games.length > 0 && (
              <div className="relative shrink-0">
                <select
                  value={selectedGameId}
                  onChange={(e) => setSelectedGameId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="bg-slate-950 border border-slate-800 text-slate-300 px-3 py-2 rounded-lg text-xs font-semibold focus:outline-none focus:border-purple-500 appearance-none cursor-pointer pr-8"
                >
                  <option value="all">Todos os Jogos</option>
                  {games.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-[10px] pointer-events-none"></i>
              </div>
            )}

            {/* Sort Dropdown */}
            <div className="relative shrink-0">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="bg-slate-950 border border-slate-800 text-slate-300 px-3 py-2 rounded-lg text-xs font-semibold focus:outline-none focus:border-purple-500 appearance-none cursor-pointer pr-8"
              >
                <option value="name_asc">Nome (A - Z)</option>
                <option value="name_desc">Nome (Z - A)</option>
                <option value="qty_desc">Maior Estoque</option>
                <option value="qty_asc">Menor Estoque</option>
                <option value="price_desc">Maior Preço</option>
                <option value="price_asc">Menor Preço</option>
              </select>
              <i className="fas fa-sort absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-[10px] pointer-events-none"></i>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-slate-950 border border-slate-800 p-0.5 rounded-lg shrink-0">
              <button
                type="button"
                onClick={() => handleToggleViewMode('table')}
                className={`px-2.5 py-1.5 rounded-md text-xs font-bold transition-colors flex items-center space-x-1.5 cursor-pointer ${
                  viewMode === 'table'
                    ? 'bg-purple-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
                title="Visualização em Tabela (ideal para FullHD)"
              >
                <i className="fas fa-table-list text-xs"></i>
                <span className="hidden sm:inline">Tabela</span>
              </button>
              <button
                type="button"
                onClick={() => handleToggleViewMode('cards')}
                className={`px-2.5 py-1.5 rounded-md text-xs font-bold transition-colors flex items-center space-x-1.5 cursor-pointer ${
                  viewMode === 'cards'
                    ? 'bg-purple-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
                title="Visualização em Grade de Cards"
              >
                <i className="fas fa-grip text-xs"></i>
                <span className="hidden sm:inline">Cards</span>
              </button>
            </div>
          </div>
        </div>

        {/* Status Filter Segmented Buttons */}
        <div className="flex items-center gap-1 overflow-x-auto pb-0.5 pt-0.5 text-xs">
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer ${
              statusFilter === 'all'
                ? 'bg-slate-800 text-white border border-slate-700'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Todos ({metrics.totalItems})
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('in_stock')}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer flex items-center space-x-1.5 ${
              statusFilter === 'in_stock'
                ? 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/40'
                : 'text-slate-400 hover:text-emerald-400'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            <span>Em Estoque ({metrics.totalItems - metrics.outOfStock})</span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('out_of_stock')}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer flex items-center space-x-1.5 ${
              statusFilter === 'out_of_stock'
                ? 'bg-rose-950/60 text-rose-300 border border-rose-500/40'
                : 'text-slate-400 hover:text-rose-400'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
            <span>Esgotados ({metrics.outOfStock})</span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('pre_sale')}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer flex items-center space-x-1.5 ${
              statusFilter === 'pre_sale'
                ? 'bg-amber-950/60 text-amber-300 border border-amber-500/40'
                : 'text-slate-400 hover:text-amber-400'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
            <span>Pré-Venda ({metrics.preSaleCount})</span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('tickets')}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer flex items-center space-x-1.5 ${
              statusFilter === 'tickets'
                ? 'bg-purple-950/60 text-purple-300 border border-purple-500/40'
                : 'text-slate-400 hover:text-purple-400'
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
            <span>Ingressos ({metrics.ticketCount})</span>
          </button>
        </div>
      </div>

      {/* Main Content: Table or Cards */}
      {processedStock.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-10 text-center space-y-3">
          <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mx-auto text-slate-500 text-lg">
            <i className="fas fa-box-open"></i>
          </div>
          <h3 className="text-white font-bold text-sm">Nenhum produto encontrado</h3>
          <p className="text-slate-500 text-xs max-w-sm mx-auto">
            {searchTerm || selectedGameId !== 'all' || statusFilter !== 'all'
              ? 'Nenhum item corresponde aos filtros selecionados. Tente limpar os filtros ou alterar a busca.'
              : 'Seu estoque ainda está vazio. Comece adicionando produtos do catálogo Cardumy.'}
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            {(searchTerm || selectedGameId !== 'all' || statusFilter !== 'all') && (
              <button
                type="button"
                onClick={() => { setSearchTerm(''); setSelectedGameId('all'); setStatusFilter('all'); }}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-bold transition-colors cursor-pointer"
              >
                Limpar Filtros
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsCatalogModalOpen(true)}
              className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
            >
              Adicionar do Catálogo
            </button>
          </div>
        </div>
      ) : viewMode === 'table' ? (
        /* TABLE VIEW (Clean, High-density, Lightweight for FullHD) */
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-950 border-b border-slate-800 text-[11px] font-bold text-slate-400 uppercase tracking-wider select-none">
                  <th className="py-2.5 px-4 w-[42%] min-w-[320px]">Produto</th>
                  <th className="py-2.5 px-4 w-[16%] min-w-[130px]">Jogo / Categoria</th>
                  <th className="py-2.5 px-4 w-[14%] min-w-[120px] text-center">Quantidade</th>
                  <th className="py-2.5 px-4 w-[14%] min-w-[130px] text-center">Preço Unitário</th>
                  <th className="py-2.5 px-3 w-[8%] min-w-[70px] text-center">Pré-Venda</th>
                  <th className="py-2.5 px-4 w-[12%] min-w-[110px] text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {paginatedStock.map((item) => {
                  const edit = editingStock[item.product_id] || { quantity: item.quantity, price: item.store_price || 0, preSale: !!item.pre_sale };
                  const ticketInfo = getTicketInfo(item);
                  const isDirty = checkIsDirty(item);
                  const isSaving = !!savingMap[item.product_id];

                  return (
                    <tr
                      key={item.id}
                      className={`transition-colors duration-100 ${
                        isDirty
                          ? 'bg-purple-950/20 hover:bg-purple-950/30'
                          : 'hover:bg-slate-800/40'
                      }`}
                    >
                      {/* Product Column */}
                      <td className="py-2.5 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="relative shrink-0 w-10 h-13 rounded-lg overflow-hidden bg-slate-950 border border-white/5">
                            <img
                              src={item.products?.image_url || 'https://via.placeholder.com/150'}
                              alt={item.products?.name || 'Produto'}
                              className="w-full h-full object-cover"
                              width={40}
                              height={52}
                              loading="lazy"
                              decoding="async"
                            />
                            {edit.quantity === 0 && (
                              <div className="absolute inset-0 bg-black/75 flex items-center justify-center">
                                <span className="text-[8px] font-black uppercase text-rose-400">Zero</span>
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center space-x-1.5 flex-wrap">
                              <span className="font-bold text-white text-xs truncate max-w-[340px] block" title={item.products?.name}>
                                {item.products?.name || 'Sem Nome'}
                              </span>
                              {ticketInfo && (
                                <span className="text-[9px] font-black uppercase px-1.5 py-0.2 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1">
                                  <i className="fas fa-ticket-alt text-[8px]"></i> Ingresso
                                </span>
                              )}
                              {edit.preSale && (
                                <span className="text-[9px] font-black uppercase px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                  Pré-Venda
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono mt-0.5 flex items-center space-x-2">
                              <span>ID #{item.product_id}</span>
                              {isDirty && (
                                <span className="text-amber-400 font-bold flex items-center gap-1">
                                  • Alterado
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Game / Category Column */}
                      <td className="py-2.5 px-4">
                        <div className="text-xs text-slate-300 font-medium">
                          {item.products?.games?.name || 'Geral / Outros'}
                        </div>
                      </td>

                      {/* Quantity Stepper Column */}
                      <td className="py-2.5 px-4">
                        {ticketInfo ? (
                          <div className="flex flex-col items-center justify-center" title="A quantidade de ingressos é fixada na capacidade do torneio">
                            <div className="inline-flex items-center space-x-1 bg-slate-950 px-2 py-0.5 rounded-lg border border-purple-500/30 text-purple-300 font-mono font-bold text-xs">
                              <i className="fas fa-lock text-[9px] text-purple-400 mr-1"></i>
                              <span>{item.quantity} un</span>
                            </div>
                            <span className="text-[9px] text-slate-500 mt-0.5">máx. {ticketInfo.maxPlayers} vagas</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <div className="inline-flex items-center bg-slate-950 border border-slate-800 rounded-lg overflow-hidden">
                              <button
                                type="button"
                                onClick={() => handleLocalChange(item.product_id, 'quantity', Math.max(0, edit.quantity - 1))}
                                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                                title="Diminuir"
                              >
                                <i className="fas fa-minus text-[9px]"></i>
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={edit.quantity ?? ''}
                                onChange={(e) => handleLocalChange(item.product_id, 'quantity', Math.max(0, parseInt(e.target.value) || 0))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSave(item.product_id);
                                }}
                                className="w-12 text-center text-xs font-mono font-bold bg-transparent text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                              <button
                                type="button"
                                onClick={() => handleLocalChange(item.product_id, 'quantity', edit.quantity + 1)}
                                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                                title="Aumentar"
                              >
                                <i className="fas fa-plus text-[9px]"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Price Column */}
                      <td className="py-2.5 px-4">
                        <div className="flex items-center justify-center">
                          <div className={`inline-flex items-center bg-slate-950 px-2 py-0.5 rounded-lg border transition-colors ${
                            edit.price !== undefined && edit.price < 0.05
                              ? 'border-amber-500/60 bg-amber-950/20'
                              : isDirty
                              ? 'border-purple-500/60'
                              : 'border-slate-800'
                          }`}>
                            <span className="text-[10px] text-slate-500 mr-1 font-bold">R$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0.05"
                              value={edit.price ?? ''}
                              onChange={(e) => handleLocalChange(item.product_id, 'price', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSave(item.product_id);
                              }}
                              className="w-16 text-xs font-mono font-bold bg-transparent text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-right"
                            />
                          </div>
                        </div>
                      </td>

                      {/* Pre-Sale Column (Simple, clean HTML checkbox) */}
                      <td className="py-2.5 px-3 text-center">
                        <label className="inline-flex items-center justify-center cursor-pointer p-1">
                          <input
                            type="checkbox"
                            checked={!!edit.preSale}
                            onChange={(e) => handleLocalChange(item.product_id, 'preSale', e.target.checked)}
                            className="w-4 h-4 rounded bg-slate-950 border border-slate-700 text-purple-600 focus:ring-0 cursor-pointer accent-purple-600"
                            title={edit.preSale ? 'Em pré-venda (clique para desmarcar)' : 'Marcar como pré-venda'}
                          />
                        </label>
                      </td>

                      {/* Actions Column */}
                      <td className="py-2.5 px-4 text-right">
                        <div className="flex items-center justify-end space-x-1.5">
                          <button
                            type="button"
                            disabled={!isDirty || isSaving}
                            onClick={() => handleSave(item.product_id)}
                            className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-colors flex items-center space-x-1 cursor-pointer ${
                              isDirty
                                ? 'bg-purple-600 hover:bg-purple-500 text-white'
                                : 'bg-slate-800 text-slate-600 border border-slate-800 opacity-40 cursor-not-allowed'
                            }`}
                            title={isDirty ? 'Salvar alterações deste produto' : 'Nenhuma alteração pendente'}
                          >
                            {isSaving ? (
                              <i className="fas fa-spinner fa-spin text-[9px]"></i>
                            ) : (
                              <i className="fas fa-check text-[9px]"></i>
                            )}
                            <span>Salvar</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleRemove(item.product_id)}
                            className="w-7 h-7 rounded-md text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors flex items-center justify-center cursor-pointer"
                            title="Remover do estoque"
                          >
                            <i className="fas fa-trash-can text-xs"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* CARDS VIEW (Clean, GPU-friendly grid for FullHD) */
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {paginatedStock.map((item) => {
            const edit = editingStock[item.product_id] || { quantity: item.quantity, price: item.store_price || 0, preSale: !!item.pre_sale };
            const ticketInfo = getTicketInfo(item);
            const isDirty = checkIsDirty(item);
            const isSaving = !!savingMap[item.product_id];

            return (
              <div
                key={item.id}
                className={`bg-slate-900 border rounded-xl p-3 flex flex-col justify-between transition-colors duration-100 ${
                  isDirty ? 'border-purple-500/60' : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                <div>
                  {/* Top card banner */}
                  <div className="flex items-start gap-2.5 mb-2.5">
                    <div className="relative shrink-0 w-11 h-15 rounded-lg overflow-hidden bg-slate-950 border border-white/5">
                      <img
                        src={item.products?.image_url || 'https://via.placeholder.com/150'}
                        alt={item.products?.name || 'Produto'}
                        className="w-full h-full object-cover"
                        width={44}
                        height={60}
                        loading="lazy"
                        decoding="async"
                      />
                      {edit.quantity === 0 && (
                        <div className="absolute inset-0 bg-black/75 flex items-center justify-center">
                          <span className="text-[7px] font-black uppercase text-rose-400">Zero</span>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-1 mb-0.5">
                        {ticketInfo ? (
                          <span className="text-[8px] font-black uppercase px-1.5 py-0.2 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            Ingresso
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold text-slate-400 truncate">
                            {item.products?.games?.name || 'Geral'}
                          </span>
                        )}
                        {edit.preSale && (
                          <span className="text-[8px] font-black uppercase px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300">
                            Pré-Venda
                          </span>
                        )}
                      </div>
                      <h4 className="font-bold text-white text-xs leading-snug line-clamp-2" title={item.products?.name}>
                        {item.products?.name || 'Sem Nome'}
                      </h4>
                      <span className="text-[10px] text-slate-500 font-mono block mt-0.5">ID #{item.product_id}</span>
                    </div>
                  </div>
                </div>

                {/* Bottom card controls */}
                <div className="space-y-2 pt-2 border-t border-slate-800">
                  <div className="grid grid-cols-2 gap-2">
                    {/* Qty */}
                    <div>
                      <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Qtd</span>
                      {ticketInfo ? (
                        <div className="h-7 bg-slate-950 px-2 rounded-lg border border-purple-500/30 flex items-center justify-between text-xs font-mono font-bold text-purple-300">
                          <span>{item.quantity}</span>
                          <i className="fas fa-lock text-[9px] text-purple-400"></i>
                        </div>
                      ) : (
                        <div className="h-7 bg-slate-950 border border-slate-800 rounded-lg flex items-center justify-between px-1">
                          <button
                            type="button"
                            onClick={() => handleLocalChange(item.product_id, 'quantity', Math.max(0, edit.quantity - 1))}
                            className="text-slate-400 hover:text-white p-1 text-[9px] cursor-pointer"
                          >
                            <i className="fas fa-minus"></i>
                          </button>
                          <input
                            type="number"
                            min="0"
                            value={edit.quantity ?? ''}
                            onChange={(e) => handleLocalChange(item.product_id, 'quantity', Math.max(0, parseInt(e.target.value) || 0))}
                            className="w-8 text-center text-xs font-mono font-bold bg-transparent text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <button
                            type="button"
                            onClick={() => handleLocalChange(item.product_id, 'quantity', edit.quantity + 1)}
                            className="text-slate-400 hover:text-white p-1 text-[9px] cursor-pointer"
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Price */}
                    <div>
                      <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Preço</span>
                      <div className="h-7 bg-slate-950 border border-slate-800 rounded-lg flex items-center px-2">
                        <span className="text-[9px] text-slate-500 mr-1 font-bold">R$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0.05"
                          value={edit.price ?? ''}
                          onChange={(e) => handleLocalChange(item.product_id, 'price', parseFloat(e.target.value) || 0)}
                          className="w-full text-xs font-mono font-bold bg-transparent text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-right"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions & Simple Pre-sale Checkbox */}
                  <div className="flex items-center justify-between pt-1">
                    <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-slate-400 hover:text-slate-200 select-none">
                      <input
                        type="checkbox"
                        checked={!!edit.preSale}
                        onChange={(e) => handleLocalChange(item.product_id, 'preSale', e.target.checked)}
                        className="w-3.5 h-3.5 rounded bg-slate-950 border border-slate-700 text-purple-600 focus:ring-0 cursor-pointer accent-purple-600"
                      />
                      <span>Pré-venda</span>
                    </label>

                    <div className="flex items-center space-x-1">
                      <button
                        type="button"
                        onClick={() => handleRemove(item.product_id)}
                        className="w-6 h-6 rounded text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors flex items-center justify-center cursor-pointer"
                        title="Remover"
                      >
                        <i className="fas fa-trash-can text-[11px]"></i>
                      </button>

                      <button
                        type="button"
                        disabled={!isDirty || isSaving}
                        onClick={() => handleSave(item.product_id)}
                        className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center space-x-1 cursor-pointer ${
                          isDirty
                            ? 'bg-purple-600 hover:bg-purple-500 text-white'
                            : 'bg-slate-800 text-slate-600 opacity-40 cursor-not-allowed'
                        }`}
                      >
                        {isSaving ? <i className="fas fa-spinner fa-spin text-[8px]"></i> : <i className="fas fa-check text-[8px]"></i>}
                        <span>Salvar</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Footer */}
      {processedStock.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-slate-400 border-t border-slate-800">
          <div className="flex items-center space-x-2">
            <span>Mostrando {paginatedStock.length} de {processedStock.length} produtos</span>
            <span className="text-slate-600">•</span>
            <span>Itens por página:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="bg-slate-900 border border-slate-800 text-slate-300 px-2 py-0.5 rounded text-xs outline-none cursor-pointer"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value="all">Todos</option>
            </select>
          </div>

          {pageSize !== 'all' && totalPages > 1 && (
            <div className="flex items-center space-x-1">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className="w-7 h-7 rounded bg-slate-900 border border-slate-800 text-slate-300 hover:text-white disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center cursor-pointer transition-colors"
                title="Página Anterior"
              >
                <i className="fas fa-chevron-left text-[9px]"></i>
              </button>
              <span className="px-2 font-mono text-slate-300 font-bold">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className="w-7 h-7 rounded bg-slate-900 border border-slate-800 text-slate-300 hover:text-white disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center cursor-pointer transition-colors"
                title="Próxima Página"
              >
                <i className="fas fa-chevron-right text-[9px]"></i>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal: New Custom Product (Zero backdrop-blur, solid overlay) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/90 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg overflow-hidden relative max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-800 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-base font-black text-white italic uppercase tracking-tight">
                  Novo Produto Avulso
                </h3>
                <p className="text-slate-400 text-xs mt-0.5">Adicione um item fora do catálogo oficial ao seu estoque.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-7 h-7 rounded-lg bg-slate-950 border border-slate-800 text-slate-400 hover:text-white flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreateCustomProduct} className="p-5 space-y-3.5 overflow-y-auto flex-1">
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-slate-400 block">Nome do Produto *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Playmat Personalizado Dragon"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:border-purple-500 transition-colors outline-none"
                  value={customProductData.name}
                  onChange={(e) => setCustomProductData({ ...customProductData, name: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Tipo de Produto *</label>
                  <div className="relative">
                    <select
                      required
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-purple-500 transition-colors appearance-none outline-none"
                      value={customProductData.product_type_id}
                      onChange={(e) => setCustomProductData({ ...customProductData, product_type_id: e.target.value })}
                    >
                      <option value="">Selecione...</option>
                      {productTypes.filter(t => t.game_id === null || !t.game_id).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-[10px] pointer-events-none"></i>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Jogo (Opcional)</label>
                  <div className="relative">
                    <select
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-purple-500 transition-colors appearance-none outline-none"
                      value={customProductData.game_id}
                      onChange={(e) => setCustomProductData({ ...customProductData, game_id: e.target.value })}
                    >
                      <option value="">Geral / Outros</option>
                      {games.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-[10px] pointer-events-none"></i>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Preço de Venda (R$) *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0.05"
                    placeholder="29.90"
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:border-purple-500 transition-colors outline-none font-mono"
                    value={customProductData.mspr}
                    onChange={(e) => setCustomProductData({ ...customProductData, mspr: e.target.value })}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Quantidade Inicial *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    placeholder="5"
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:border-purple-500 transition-colors outline-none font-mono"
                    value={customProductData.quantity}
                    onChange={(e) => setCustomProductData({ ...customProductData, quantity: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-slate-400 block">URL da Imagem (Opcional)</label>
                <input
                  type="url"
                  placeholder="https://exemplo.com/imagem.png"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:border-purple-500 transition-colors outline-none"
                  value={customProductData.image_url}
                  onChange={(e) => setCustomProductData({ ...customProductData, image_url: e.target.value })}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-slate-400 block">Descrição (Opcional)</label>
                <textarea
                  rows={2}
                  placeholder="Detalhes adicionais do produto..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:border-purple-500 transition-colors outline-none resize-none"
                  value={customProductData.description}
                  onChange={(e) => setCustomProductData({ ...customProductData, description: e.target.value })}
                />
              </div>

              <div className="pt-2 flex space-x-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 bg-slate-950 border border-slate-800 hover:bg-slate-800 text-slate-400 hover:text-white font-bold py-2 rounded-lg text-xs transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold py-2 rounded-lg text-xs transition-colors cursor-pointer"
                >
                  {submitting ? 'Cadastrando...' : 'Cadastrar Produto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Catalog Modal */}
      {store?.id && (
        <AddCatalogProductModal
          isOpen={isCatalogModalOpen}
          onClose={() => setIsCatalogModalOpen(false)}
          storeId={store.id}
          existingStock={stock}
          onStockUpdated={handleStockFromCatalog}
        />
      )}
    </div>
  );
};
